import { HttpHeaders } from '@angular/common/http';
import MainScene from '../Scenes/MainScene';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'environment';
import { AuthSession, fetchAuthSession } from 'aws-amplify/auth';
import ChunkedScene from 'src/classes/ChunkedScene';
import { showToast } from 'src/classes/Utilities';
import { FilePicker } from '@capawesome/capacitor-file-picker';
import { UserAudio } from 'infrastructure/lib/src/models/user-audio';

// Show toast

export class AudioService {
  static audio$ = new BehaviorSubject<UserAudio[]>([]);
  static identityId: string = '';
  static username: string = '';

  constructor(){

  }

  static async fileToDataUrl(file: Blob): Promise<string> { 
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  static async generateStandardizeAudioName(playerCoords: Phaser.Math.Vector2): Promise<{s3Path: string, chunkId: string}> {
    try{
      const chunkId = ChunkedScene.computeChunkID(playerCoords.x, playerCoords.y);
      console.log(`chunkId: ${chunkId}`);
      return {s3Path: `${playerCoords.x}_${playerCoords.y}_${Date.now()}`, chunkId: String(chunkId)};
    } catch (error) {
      console.error('Error generating audio name:', error);
      throw error;
    }
  }

  static async uploadAudio(audioBlob: Blob, originalFileName: string, description?: string): Promise<void> {
    try {

      // Prefer currentSession for a token
      const session: AuthSession = await fetchAuthSession();
      const auth = `Bearer ${session.tokens.idToken}`;

      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': auth
      });

      // Create a filename based on the player's current position and timestamp.
      const {x, y} = MainScene.playerMetadata.playerPosition;
      const {s3Path, chunkId} = await AudioService.generateStandardizeAudioName(new Phaser.Math.Vector2(x, y));

      const payload = {
        audio_data: await AudioService.fileToDataUrl(audioBlob),
        chunkId: chunkId,
      };

      console.log('Uploading audio with payload:', payload);
      console.log('originalFileName:', originalFileName);
      console.log('chunkId:', chunkId);
      console.log('environment.REST_API:', environment.REST_API);

      const resp = await fetch(`${environment.REST_API}audio?s3Path=${s3Path}&filename=${originalFileName}`, {
        method: 'POST',
        headers: {Authorization: auth},
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        throw new Error(`Failed to upload audio: ${resp.status} ${resp.statusText}`);
      }

      console.log('Audio uploaded successfully');
      showToast({message: 'Audio uploaded successfully', color: 'success', duration: 2000});

    } catch (error) {
      console.error('Error uploading audio:', error);
      showToast({message: 'Error uploading audio', color: 'danger', duration: 2000});
      throw error;
    }
  }

  static async selectAudio(): Promise<{blob: Blob, fileName: string} | null> {
    try {
      const result = await FilePicker.pickFiles({
        types: ['audio/*'],
        limit: 1,
      });

      if (result && result.files.length > 0) {
        try{
          console.log('Selected audio file:', result.files[0]);
          AudioService.uploadAudio(result.files[0].blob, result.files[0].name);
        } catch(error){
          console.error('Error uploading selected audio:', error);
        }
      }
    } catch (error) {
      console.error('Error selecting audio file:', error);
      showToast({message: 'Error selecting audio file', color: 'danger', duration: 2000});
      return null;
    }
  }

  public static async getAllAudioNames(username: string='') {
    try {
      // Use the /audioGetByUsername lambda api to get audio metadata
      const resp = await fetch(`${environment.REST_API}audioGetByUsername?username=${username}`,
        {
          method: 'GET',
        }
      );
      
      if (!resp.ok) {
        throw new Error(`Failed to get audio metadata: ${resp.status} ${resp.statusText}`);
      }

      const response = await resp.json();
      console.log('getAllAudioNames result:', response);

      // render list items from response.items
      return response.items as Array<UserAudio>;
      // return publicAudioList
      //   ? publicAudioList.map((a) => {
      //       return a.uploader + '/' + a.s3Path;
      //     })
      //   : [];

    } catch (error) {
      console.error('Error ', error);
    }
  }

  static async getAudiosByUser(username: string): Promise<Array<UserAudio>>{
    const audioMetadataList = await AudioService.getAllAudioNames(username);
    let audios = Array<UserAudio>();
    audioMetadataList.forEach(async (audioMetadata) => {
      const s3AudioSignedURL = await fetch(`${environment.REST_API}get-signed-url?bucket=st-audio-bucket&key=${audioMetadata.s3Path}`);
      const audioSrc = (await s3AudioSignedURL.json()).signedUrl!.toString();
      console.log('audioSrc:', audioSrc);
      audios.push({...audioMetadata, webviewPath: audioSrc});
    });
    return audios;
  }

  public static async syncAudio(): Promise<void> {
    try {
      const audiosByUser = await AudioService.getAudiosByUser(AudioService.username);
      console.log('syncAudio audiosByUser:', audiosByUser);
      AudioService.audio$.next(audiosByUser);
    } catch (error) {
      console.error('Error syncing audio:', error);
      throw error;
    }
  } 

  public async deleteAudio(s3Path: string): Promise<boolean> {
    try {

      const session = await fetchAuthSession();
      const auth = `Bearer ${session.tokens.idToken}`;

      // user the /audio lambda api to delete the audio
      const resp = await fetch(`${environment.REST_API}audio?s3Path=${s3Path}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: auth
          },
        }
      );
      
      if (!resp.ok) {
        throw new Error(`Failed to delete audio: ${resp.status} ${resp.statusText}`);
      }

      // Show toast and remove from local display
      showToast({message: 'Audio deleted successfully', color: 'success', duration: 2000});

      // Remove the audio from the local audio display
      function removeValue(audio: UserAudio, index, arr) {
        // If the value at the current array index matches the specified value
        if (audio.s3Path === s3Path) {
          // Removes the value from the original array
          arr.splice(index, 1);
          return true;
        }
        return false;
      }
      const audios = AudioService.audio$.getValue();
      audios.filter(removeValue);
      AudioService.audio$.next(audios);

      return true;
    } catch (error) {
      console.error('Error ', error);
      showToast({message: `Error deleting audio`, color: 'danger', duration: 2000});
      return false;
    }
  }

}